import { STORAGE_KEYS, storageGetItem, storageSetItem } from '../internal/storageAdapter';
import { isPushSupported, registerServiceWorker } from './pushManager';

const DEFAULT_CDN_BASE = 'https://motisig.ai/cdn/web/v1';

export function resolveCdnVersionUrl(cdnBase?: string): string {
  const base = (cdnBase?.trim() || DEFAULT_CDN_BASE).replace(/\/$/, '');
  return `${base}/version.json`;
}

export function cdnBaseFromScriptSrc(scriptSrc: string): string | undefined {
  try {
    const u = new URL(scriptSrc);
    const dir = u.pathname.replace(/\/[^/]*$/, '');
    return `${u.origin}${dir}`;
  } catch {
    return undefined;
  }
}

export async function fetchRemoteSwVersion(cdnVersionUrl: string): Promise<string | null> {
  try {
    const res = await fetch(cdnVersionUrl, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { sw?: unknown };
    const sw = typeof body.sw === 'string' ? body.sw.trim() : '';
    return sw || null;
  } catch {
    return null;
  }
}

export async function syncPushServiceWorker(opts: {
  swPath: string;
  swScope: string;
  cdnBaseUrl?: string;
}): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;

  const cdnVersionUrl = resolveCdnVersionUrl(opts.cdnBaseUrl);
  const remote = await fetchRemoteSwVersion(cdnVersionUrl);
  const local = storageGetItem(STORAGE_KEYS.swInstalledVersion);

  let reg = await navigator.serviceWorker.getRegistration(opts.swScope);

  if (reg && remote && remote !== local) {
    await reg.unregister();
    reg = undefined;
  }

  if (!reg) {
    reg = await registerServiceWorker(opts.swPath, opts.swScope);
  } else {
    await reg.update();
  }

  await navigator.serviceWorker.ready;

  if (remote) {
    storageSetItem(STORAGE_KEYS.swInstalledVersion, remote);
  }

  return reg;
}
